import React, { useState } from 'react';
import { GraduationCap, Book, FileText, Calendar, CheckCircle, Download, Users, Target, Award, TrendingUp, Clock, BookOpen, PenTool, Brain, Globe, Calculator, School, Library, Lightbulb, Trophy, ChevronDown, ChevronUp } from 'lucide-react';

const CUETExamPage = () => {
  const [selectedExam, setSelectedExam] = useState('CUET_UG');
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const examCategories = {
    'CUET_UG': {
      name: "CUET (UG)",
      fullName: "Common University Entrance Test - Undergraduate",
      posts: "For Admission to Central Universities",
      eligibility: "12th Pass / Appearing Students",
      icon: <GraduationCap />,
      color: "#8B5CF6",
      gradient: "linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)"
    },
    'CUET_PG': {
      name: "CUET (PG)",
      fullName: "Common University Entrance Test - Postgraduate",
      posts: "For Admission to PG Programs",
      eligibility: "Graduate / Final Year Students",
      icon: <School />,
      color: "#3B82F6",
      gradient: "linear-gradient(135deg, #3B82F6 0%, #06B6D4 100%)"
    }
  };

  const examPattern = {
    CUET_UG: {
      structure: {
        name: "CUET UG Exam Structure",
        duration: "Multiple Sessions",
        mode: "Computer Based Test (CBT)",
        sections: [
          {
            section: "Section IA",
            subject: "Languages (13 Options)",
            questions: 50,
            attempt: 40,
            marks: 200,
            time: "45 min"
          },
          {
            section: "Section IB",
            subject: "Languages (20 Options)",
            questions: 50,
            attempt: 40,
            marks: 200,
            time: "45 min"
          },
          {
            section: "Section II",
            subject: "Domain Subjects (27 Options)",
            questions: 50,
            attempt: 40,
            marks: 200,
            time: "45 min each"
          },
          {
            section: "Section III",
            subject: "General Test",
            questions: 75,
            attempt: 60,
            marks: 300,
            time: "60 min"
          }
        ],
        negativeMarking: "1 mark deducted for each wrong answer",
        note: "Candidates can choose maximum 4 subjects from Section II"
      }
    },
    CUET_PG: {
      structure: {
        name: "CUET PG Exam Structure",
        duration: "120 Minutes",
        mode: "Computer Based Test (CBT)",
        sections: [
          {
            section: "Part A",
            subject: "Domain Knowledge",
            questions: 75,
            marks: 300,
            description: "Subject-specific questions"
          },
          {
            section: "Part B",
            subject: "Research Methodology",
            questions: 25,
            marks: 100,
            description: "For research programs only"
          }
        ],
        negativeMarking: "1 mark deducted for each wrong answer",
        note: "Part B is applicable only for Research/PhD programs"
      }
    }
  };

  const domainSubjects = [
    "Accountancy", "Anthropology", "Biology/Biological Studies",
    "Business Studies", "Chemistry", "Computer Science",
    "Economics/Business Economics", "Engineering Graphics",
    "Entrepreneurship", "Environmental Studies", "Fine Arts",
    "Geography/Geology", "History", "Home Science",
    "Knowledge Tradition-Practices India", "Legal Studies",
    "Mass Media/Mass Communication", "Mathematics",
    "Painting", "Physical Education/NCC/Yoga",
    "Physics", "Political Science", "Psychology",
    "Sanskrit", "Sociology", "Teaching Aptitude"
  ];

  const languages = {
    sectionIA: [
      "Tamil", "Telugu", "Kannada", "Malayalam", "Marathi",
      "Gujarati", "Odia", "Bengali", "Assamese", "Punjabi",
      "English", "Hindi", "Urdu"
    ],
    sectionIB: [
      "Arabic", "Bodo", "Chinese", "Dogri", "French",
      "German", "Italian", "Japanese", "Kashmiri", "Konkani",
      "Maithili", "Manipuri", "Nepali", "Persian", "Russian",
      "Santhali", "Sindhi", "Spanish", "Tibetan", "Sanskrit"
    ]
  };

  const generalTestTopics = [
    "General Knowledge",
    "Current Affairs",
    "General Mental Ability",
    "Numerical Ability",
    "Quantitative Reasoning",
    "Logical & Analytical Reasoning"
  ];

  const participatingUniversities = [
    "Jawaharlal Nehru University (JNU)",
    "Delhi University (DU)",
    "Banaras Hindu University (BHU)",
    "Aligarh Muslim University (AMU)",
    "Jamia Millia Islamia",
    "Central University of Hyderabad",
    "Central University of Rajasthan",
    "Central University of Gujarat",
    "Central University of Kerala",
    "Central University of Karnataka",
    "Central University of Tamil Nadu",
    "Central University of Punjab",
    "Central University of Haryana",
    "Central University of Jharkhand",
    "Central University of Jammu"
  ];

  const benefits = [
    {
      icon: <School />,
      title: "Single Exam, Multiple Universities",
      desc: "One exam for admission to 250+ central & participating universities"
    },
    {
      icon: <Award />,
      title: "Fair & Transparent",
      desc: "National-level standardized test with equal opportunities"
    },
    {
      icon: <BookOpen />,
      title: "Wide Subject Choices",
      desc: "Choose from 33 languages and 27 domain subjects"
    },
    {
      icon: <Trophy />,
      title: "Merit-Based Selection",
      desc: "Admission purely based on CUET scores and merit"
    },
    {
      icon: <Globe />,
      title: "Uniform Evaluation",
      desc: "Standardized assessment across India"
    },
    {
      icon: <Target />,
      title: "Better Opportunities",
      desc: "Access to top central universities nationwide"
    }
  ];

  const preparationTips = [
    {
      icon: <Book />,
      title: "NCERT Mastery",
      desc: "Focus on NCERT textbooks for Classes 11-12 thoroughly"
    },
    {
      icon: <Clock />,
      title: "Time Management",
      desc: "Practice with timer to improve speed and accuracy"
    },
    {
      icon: <PenTool />,
      title: "Mock Tests",
      desc: "Attempt 50+ full-length mock tests before exam"
    },
    {
      icon: <Globe />,
      title: "Current Affairs",
      desc: "Read newspapers daily for General Test preparation"
    },
    {
      icon: <Brain />,
      title: "Concept Clarity",
      desc: "Focus on understanding concepts rather than memorization"
    },
    {
      icon: <FileText />,
      title: "Previous Papers",
      desc: "Solve last year papers to understand exam pattern"
    }
  ];

  const importantDates = {
    CUET_UG: [
      { event: "Notification Release", date: "February - March", status: "Official" },
      { event: "Online Registration", date: "March - April", status: "Apply" },
      { event: "Application Correction", date: "April", status: "Edit" },
      { event: "Admit Card Release", date: "May", status: "Download" },
      { event: "Exam Date", date: "May (Multiple Slots)", status: "Exam" },
      { event: "Answer Key Release", date: "June", status: "Check" },
      { event: "Result Declaration", date: "June", status: "Result" },
      { event: "Counselling Process", date: "July - August", status: "Admission" }
    ],
    CUET_PG: [
      { event: "Notification Release", date: "February", status: "Official" },
      { event: "Online Registration", date: "March", status: "Apply" },
      { event: "Application Correction", date: "March - April", status: "Edit" },
      { event: "Admit Card Release", date: "April", status: "Download" },
      { event: "Exam Date", date: "May", status: "Exam" },
      { event: "Answer Key Release", date: "May", status: "Check" },
      { event: "Result Declaration", date: "June", status: "Result" },
      { event: "University Counselling", date: "June - August", status: "Admission" }
    ]
  };

  const eligibilityCriteria = {
    CUET_UG: {
      age: "No upper age limit",
      qualification: "Passed/Appearing in Class 12th or equivalent from recognized board",
      attempts: "No restriction on number of attempts",
      subjects: "Must have studied required subjects in 10+2 as per university requirements"
    },
    CUET_PG: {
      age: "No upper age limit (as per university norms)",
      qualification: "Graduate degree in relevant stream from recognized university",
      attempts: "No restriction on number of attempts",
      percentage: "Minimum 50% marks in graduation (45% for reserved categories)"
    }
  };

  const applicationFees = {
    general: {
      "1 Test": "₹650",
      "2 Tests": "₹1,200",
      "3 Tests": "₹1,750",
      "4+ Tests": "₹2,300"
    },
    reserved: {
      "1 Test": "₹600",
      "2 Tests": "₹1,100",
      "3 Tests": "₹1,600",
      "4+ Tests": "₹2,100"
    }
  };

  const faqs = [
    {
      q: "What is CUET and why was it introduced?",
      a: "CUET (Common University Entrance Test) is a single-window entrance examination for admission to undergraduate and postgraduate programs in central universities across India. It was introduced by NTA in 2022 to provide a standardized admission process, ensuring fair and merit-based selection. Previously, each university conducted its own entrance test, which was time-consuming and expensive for students."
    },
    {
      q: "How many subjects can I choose in CUET UG?",
      a: "In CUET UG, you can choose up to 2 languages from Section IA/IB, up to 4 Domain Subjects from Section II, and the General Test from Section III is optional but recommended. Most universities require 1 language + General Test + 3 Domain Subjects. Check your target university's requirements before selecting subjects."
    },
    {
      q: "Is CUET difficult compared to board exams?",
      a: "CUET is moderately difficult and different from board exams. While board exams test your knowledge, CUET tests your aptitude, speed, and analytical skills. The syllabus is similar to Class 12 NCERT, but questions are MCQ-based with negative marking. With proper preparation of 4-6 months, students can score well."
    },
    {
      q: "Can I appear for CUET after a gap year?",
      a: "Yes, there is no age limit for CUET. Students who have completed Class 12 in previous years can also appear. This applies to both CUET UG and PG. Many students take gap years to prepare specifically for CUET to get admission in their dream university and course."
    },
    {
      q: "Which universities accept CUET scores?",
      a: "Over 250+ universities accept CUET scores including all 45 Central Universities, State Universities, Deemed Universities, and Private Universities. Major universities include Delhi University, JNU, BHU, AMU, Jamia Millia Islamia, IIIT Hyderabad, and many more. The complete list is available on the official CUET website."
    },
    {
      q: "What is the difference between CUET UG and CUET PG?",
      a: "CUET UG is for undergraduate admissions after Class 12, covering Languages, Domain Subjects, and General Test. CUET PG is for postgraduate admissions after graduation, focusing on domain knowledge and research methodology (for PhD programs). The exam pattern, difficulty level, and syllabus are different for both."
    },
    {
      q: "Is General Test compulsory in CUET UG?",
      a: "General Test is not compulsory for all programs. However, many universities like Delhi University require General Test scores for admission. It's highly recommended to appear for General Test as it increases your university options. Check your target university's admission criteria."
    },
    {
      q: "Can I change my subjects after registration?",
      a: "Yes, NTA provides a correction window (usually 2-3 days) after registration closes where you can modify your chosen subjects, upload documents, and make other changes. However, a correction fee applies. After the correction window, no changes are allowed."
    },
    {
      q: "What is the validity of CUET score?",
      a: "CUET scores are valid only for the current admission cycle (same year). If you don't get admission or wish to apply again, you need to appear for CUET again in the next year. Scores cannot be carried forward to subsequent years."
    },
    {
      q: "How is CUET score calculated?",
      a: "CUET follows a normalization process using percentile scores. Raw marks are converted to percentile based on the difficulty level of your session. Universities use these normalized scores for merit list preparation. Each section is evaluated separately, and universities may assign different weightage to different sections."
    }
  ];

  return (
    <div style={{ background: '#f8f9fa', minHeight: '100vh' }}>
      {/* Hero Section */}
      <div style={{
        background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 50%, #F59E0B 100%)',
        padding: '80px 20px 60px',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ fontSize: '4rem', marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
            <GraduationCap size={80} />
          </div>
          <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 'bold', marginBottom: '20px' }}>
            CUET Exam Preparation 2025
          </h1>
          <p style={{ fontSize: 'clamp(1.1rem, 3vw, 1.4rem)', marginBottom: '15px', opacity: 0.95 }}>
            Common University Entrance Test - Your Gateway to Top Universities
          </p>
          <p style={{ fontSize: 'clamp(1rem, 2.5vw, 1.2rem)', marginBottom: '30px', opacity: 0.9 }}>
            250+ Participating Universities | 45+ Central Universities
          </p>
          <div style={{
            background: 'rgba(255,255,255,0.2)',
            padding: '20px 25px',
            borderRadius: '10px',
            display: 'inline-block',
            marginBottom: '30px',
            maxWidth: '900px'
          }}>
            <p style={{ fontSize: 'clamp(0.9rem, 2vw, 1.1rem)', margin: 0, lineHeight: '1.6' }}>
              <strong>🎓 CUET UG:</strong> For UG Admissions | <strong>📚 CUET PG:</strong> For PG & Research Programs<br/>
              <strong>📝 Exam Mode:</strong> Computer Based Test (CBT) | <strong>🗓️ Exam Date:</strong> May 2025
            </p>
          </div>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => alert('Enrollment functionality')}
              style={{
                background: 'white',
                color: '#8B5CF6',
                padding: '15px 40px',
                borderRadius: '30px',
                border: 'none',
                fontSize: 'clamp(1rem, 2vw, 1.1rem)',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-3px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              Start CUET Preparation
            </button>
            <button
              onClick={() => document.getElementById('pattern').scrollIntoView({ behavior: 'smooth' })}
              style={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                padding: '15px 40px',
                borderRadius: '30px',
                border: '2px solid white',
                fontSize: 'clamp(1rem, 2vw, 1.1rem)',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#8B5CF6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                e.currentTarget.style.color = 'white';
              }}
            >
              View Exam Pattern
            </button>
          </div>
        </div>
      </div>

      {/* Exam Selection Tabs */}
      <div style={{ 
        background: 'white', 
        padding: '30px 20px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
        position: 'relative',
        top: '0',
        zIndex: 100
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px'
          }}>
            {Object.keys(examCategories).map(exam => (
              <button
                key={exam}
                onClick={() => setSelectedExam(exam)}
                style={{
                  padding: '25px',
                  borderRadius: '12px',
                  border: 'none',
                  background: selectedExam === exam 
                    ? examCategories[exam].gradient
                    : '#f0f0f0',
                  color: selectedExam === exam ? 'white' : '#333',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: selectedExam === exam ? `0 4px 15px ${examCategories[exam].color}40` : 'none',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  if (selectedExam !== exam) {
                    e.currentTarget.style.background = '#e0e0e0';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedExam !== exam) {
                    e.currentTarget.style.background = '#f0f0f0';
                  }
                }}
              >
                <div style={{ fontSize: '2.5rem', marginBottom: '15px', display: 'flex' }}>
                  {examCategories[exam].icon}
                </div>
                <div style={{ fontSize: '1.3rem', marginBottom: '8px' }}>
                  {examCategories[exam].name}
                </div>
                <div style={{ fontSize: '0.95rem', opacity: selectedExam === exam ? 0.9 : 0.7 }}>
                  {examCategories[exam].eligibility}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' }}>
        
        {/* Exam Overview */}
        <section style={{ marginBottom: '60px' }}>
          <div style={{
            background: 'white',
            padding: 'clamp(20px, 5vw, 40px)',
            borderRadius: '20px',
            boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
            border: `3px solid ${examCategories[selectedExam].color}`
          }}>
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <div style={{ 
                fontSize: '3.5rem', 
                color: examCategories[selectedExam].color,
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'center'
              }}>
                {examCategories[selectedExam].icon}
              </div>
              
              <h2 style={{ 
                fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', 
                fontWeight: 'bold', 
                marginBottom: '10px',
                color: '#2c3e50'
              }}>
                {examCategories[selectedExam].name}
              </h2>
              
              <p style={{ 
                fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                color: '#666',
                marginBottom: '10px'
              }}>
                {examCategories[selectedExam].fullName}
              </p>

              <p style={{ 
                fontSize: 'clamp(1.1rem, 2.5vw, 1.3rem)',
                color: examCategories[selectedExam].color,
                fontWeight: '600',
                marginTop: '20px'
              }}>
                {examCategories[selectedExam].posts}
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '25px'
            }}>
              <div style={{
                padding: '25px',
                background: `${examCategories[selectedExam].color}10`,
                borderRadius: '15px',
                border: `2px solid ${examCategories[selectedExam].color}30`
              }}>
                <h3 style={{ 
                  fontSize: 'clamp(1.1rem, 2.5vw, 1.3rem)', 
                  fontWeight: 'bold', 
                  marginBottom: '15px',
                  color: examCategories[selectedExam].color,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <School size={24} />
                  Eligibility
                </h3>
                <p style={{ 
                  fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                  color: '#555',
                  margin: 0,
                  lineHeight: '1.6'
                }}>
                  {eligibilityCriteria[selectedExam].qualification}
                </p>
              </div>

              <div style={{
                padding: '25px',
                background: '#e3f2fd',
                borderRadius: '15px',
                border: '2px solid #2196f3'
              }}>
                <h3 style={{ 
                  fontSize: 'clamp(1.1rem, 2.5vw, 1.3rem)', 
                  fontWeight: 'bold', 
                  marginBottom: '15px',
                  color: '#1565c0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <Calendar size={24} />
                  Age Limit
                </h3>
                <p style={{ 
                  fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                  color: '#555',
                  margin: 0,
                  lineHeight: '1.6'
                }}>
                  {eligibilityCriteria[selectedExam].age}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            Why Choose CUET?
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '30px'
          }}>
            {benefits.map((benefit, idx) => (
              <div key={idx} style={{
                background: 'white',
                padding: 'clamp(20px, 4vw, 35px)',
                borderRadius: '15px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                border: '2px solid transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-10px)';
                e.currentTarget.style.boxShadow = `0 12px 30px ${examCategories[selectedExam].color}30`;
                e.currentTarget.style.borderColor = examCategories[selectedExam].color;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
                e.currentTarget.style.borderColor = 'transparent';
              }}>
                <div style={{ 
                  fontSize: '3.5rem',
                  color: examCategories[selectedExam].color,
                  marginBottom: '20px',
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  {benefit.icon}
                </div>
                <h3 style={{ 
                  fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)',
                  fontWeight: 'bold',
                  marginBottom: '15px',
                  color: '#2c3e50'
                }}>
                  {benefit.title}
                </h3>
                <p style={{ 
                  color: '#666',
                  lineHeight: '1.6',
                  margin: 0,
                  fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                }}>
                  {benefit.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Exam Pattern */}
        <section id="pattern" style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            {examPattern[selectedExam].structure.name}
          </h2>

          <div style={{
            background: 'white',
            padding: 'clamp(20px, 4vw, 40px)',
            borderRadius: '20px',
            boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
            marginBottom: '30px'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px',
              marginBottom: '30px'
            }}>
              <div style={{
                padding: '20px',
                background: `${examCategories[selectedExam].color}10`,
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <Clock size={32} style={{ color: examCategories[selectedExam].color, marginBottom: '10px' }} />
                <h4 style={{ fontSize: '1rem', color: '#666', marginBottom: '8px' }}>Duration</h4>
                <p style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#2c3e50', margin: 0 }}>
                  {examPattern[selectedExam].structure.duration}
                </p>
              </div>

              <div style={{
                padding: '20px',
                background: '#e8f5e9',
                borderRadius: '12px',
                textAlign: 'center'
              }}>
                <FileText size={32} style={{ color: '#4CAF50', marginBottom: '10px' }} />
                <h4 style={{ fontSize: '1rem', color: '#666', marginBottom: '8px' }}>Mode</h4>
                <p style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#2c3e50', margin: 0 }}>
                  {examPattern[selectedExam].structure.mode}
                </p>
              </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                minWidth: '600px'
              }}>
                <thead>
                  <tr style={{ background: examCategories[selectedExam].gradient }}>
                    <th style={{ padding: '15px', color: 'white', textAlign: 'left', fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>Section</th>
                    <th style={{ padding: '15px', color: 'white', textAlign: 'left', fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>Subject</th>
                    <th style={{ padding: '15px', color: 'white', textAlign: 'center', fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>Questions</th>
                    <th style={{ padding: '15px', color: 'white', textAlign: 'center', fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>Marks</th>
                    <th style={{ padding: '15px', color: 'white', textAlign: 'center', fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {examPattern[selectedExam].structure.sections.map((section, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #e0e0e0' }}>
                      <td style={{ padding: '15px', fontWeight: 'bold', color: examCategories[selectedExam].color, fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)' }}>
                        {section.section}
                      </td>
                      <td style={{ padding: '15px', fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)' }}>
                        {section.subject}
                        {section.description && <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '5px' }}>{section.description}</div>}
                      </td>
                      <td style={{ padding: '15px', textAlign: 'center', fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)' }}>
                        {section.questions}
                        {section.attempt && <div style={{ fontSize: '0.8rem', color: '#666' }}>(Attempt {section.attempt})</div>}
                      </td>
                      <td style={{ padding: '15px', textAlign: 'center', fontWeight: 'bold', fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)' }}>
                        {section.marks}
                      </td>
                      <td style={{ padding: '15px', textAlign: 'center', fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)' }}>
                        {section.time}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{
              marginTop: '25px',
              padding: '20px',
              background: '#fff3cd',
              borderRadius: '10px',
              border: '2px solid #ffc107'
            }}>
              <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#856404', fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>
                ⚠️ Important Notes:
              </p>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#856404', fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)' }}>
                <li>{examPattern[selectedExam].structure.negativeMarking}</li>
                <li>{examPattern[selectedExam].structure.note}</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Domain Subjects & Languages for CUET UG */}
        {selectedExam === 'CUET_UG' && (
          <>
            <section style={{ marginBottom: '60px' }}>
              <h2 style={{ 
                fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', 
                fontWeight: 'bold', 
                marginBottom: '30px',
                color: '#2c3e50',
                textAlign: 'center'
              }}>
                Domain Subjects (Section II)
              </h2>

              <div style={{
                background: 'white',
                padding: 'clamp(20px, 4vw, 40px)',
                borderRadius: '20px',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
              }}>
                <p style={{ 
                  fontSize: 'clamp(0.95rem, 2vw, 1.1rem)', 
                  color: '#666', 
                  marginBottom: '25px',
                  textAlign: 'center'
                }}>
                  Choose maximum 4 subjects from the following 27 domain-specific subjects:
                </p>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '15px'
                }}>
                  {domainSubjects.map((subject, idx) => (
                    <div key={idx} style={{
                      padding: '15px',
                      background: `${examCategories[selectedExam].color}10`,
                      borderRadius: '10px',
                      border: `2px solid ${examCategories[selectedExam].color}30`,
                      textAlign: 'center',
                      fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)',
                      fontWeight: '500',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = examCategories[selectedExam].color;
                      e.currentTarget.style.color = 'white';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = `${examCategories[selectedExam].color}10`;
                      e.currentTarget.style.color = 'inherit';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}>
                      {subject}
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section style={{ marginBottom: '60px' }}>
              <h2 style={{ 
                fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', 
                fontWeight: 'bold', 
                marginBottom: '30px',
                color: '#2c3e50',
                textAlign: 'center'
              }}>
                Language Options
              </h2>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '30px'
              }}>
                <div style={{
                  background: 'white',
                  padding: 'clamp(20px, 4vw, 30px)',
                  borderRadius: '20px',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{
                    fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)',
                    fontWeight: 'bold',
                    marginBottom: '20px',
                    color: examCategories[selectedExam].color,
                    textAlign: 'center'
                  }}>
                    Section IA Languages
                  </h3>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                  }}>
                    {languages.sectionIA.map((lang, idx) => (
                      <div key={idx} style={{
                        padding: '12px',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)',
                        borderLeft: `4px solid ${examCategories[selectedExam].color}`
                      }}>
                        {lang}
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{
                  background: 'white',
                  padding: 'clamp(20px, 4vw, 30px)',
                  borderRadius: '20px',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                }}>
                  <h3 style={{
                    fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)',
                    fontWeight: 'bold',
                    marginBottom: '20px',
                    color: '#3B82F6',
                    textAlign: 'center'
                  }}>
                    Section IB Languages
                  </h3>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                  }}>
                    {languages.sectionIB.map((lang, idx) => (
                      <div key={idx} style={{
                        padding: '12px',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)',
                        borderLeft: '4px solid #3B82F6'
                      }}>
                        {lang}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            <section style={{ marginBottom: '60px' }}>
              <h2 style={{ 
                fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', 
                fontWeight: 'bold', 
                marginBottom: '30px',
                color: '#2c3e50',
                textAlign: 'center'
              }}>
                General Test Topics (Section III)
              </h2>

              <div style={{
                background: 'white',
                padding: 'clamp(20px, 4vw, 40px)',
                borderRadius: '20px',
                boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '20px'
                }}>
                  {generalTestTopics.map((topic, idx) => (
                    <div key={idx} style={{
                      padding: '25px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '15px',
                      color: 'white',
                      textAlign: 'center',
                      fontSize: 'clamp(0.95rem, 2vw, 1.1rem)',
                      fontWeight: '600',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
                    }}>
                      {topic}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </>
        )}

        {/* Participating Universities */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            Top Participating Universities
          </h2>

          <div style={{
            background: 'white',
            padding: 'clamp(20px, 4vw, 40px)',
            borderRadius: '20px',
            boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
          }}>
            <p style={{ 
              fontSize: 'clamp(0.95rem, 2vw, 1.1rem)', 
              color: '#666', 
              marginBottom: '30px',
              textAlign: 'center'
            }}>
              Over 250+ universities across India accept CUET scores for admissions
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '20px'
            }}>
              {participatingUniversities.map((university, idx) => (
                <div key={idx} style={{
                  padding: '20px',
                  background: idx % 3 === 0 ? '#e8f5e9' : idx % 3 === 1 ? '#e3f2fd' : '#fce4ec',
                  borderRadius: '12px',
                  borderLeft: `5px solid ${idx % 3 === 0 ? '#4CAF50' : idx % 3 === 1 ? '#2196F3' : '#E91E63'}`,
                  fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateX(10px)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}>
                  <Library size={20} style={{ color: idx % 3 === 0 ? '#4CAF50' : idx % 3 === 1 ? '#2196F3' : '#E91E63', flexShrink: 0 }} />
                  {university}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Application Fees */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            Application Fee Structure
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px'
          }}>
            <div style={{
              background: 'white',
              padding: 'clamp(20px, 4vw, 35px)',
              borderRadius: '20px',
              boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
              border: '3px solid #4CAF50'
            }}>
              <h3 style={{
                fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)',
                fontWeight: 'bold',
                marginBottom: '20px',
                color: '#4CAF50',
                textAlign: 'center'
              }}>
                General / OBC / EWS
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {Object.entries(applicationFees.general).map(([tests, fee], idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '15px',
                    background: '#f8f9fa',
                    borderRadius: '10px',
                    fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                  }}>
                    <span style={{ fontWeight: '600' }}>{tests}</span>
                    <span style={{ fontWeight: 'bold', color: '#4CAF50' }}>{fee}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{
              background: 'white',
              padding: 'clamp(20px, 4vw, 35px)',
              borderRadius: '20px',
              boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
              border: '3px solid #2196F3'
            }}>
              <h3 style={{
                fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)',
                fontWeight: 'bold',
                marginBottom: '20px',
                color: '#2196F3',
                textAlign: 'center'
              }}>
                SC / ST / PwD / Third Gender
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {Object.entries(applicationFees.reserved).map(([tests, fee], idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '15px',
                    background: '#f8f9fa',
                    borderRadius: '10px',
                    fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                  }}>
                    <span style={{ fontWeight: '600' }}>{tests}</span>
                    <span style={{ fontWeight: 'bold', color: '#2196F3' }}>{fee}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Preparation Tips */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            Preparation Strategy
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '30px'
          }}>
            {preparationTips.map((tip, idx) => (
              <div key={idx} style={{
                background: 'white',
                padding: 'clamp(20px, 4vw, 35px)',
                borderRadius: '15px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
                textAlign: 'center',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                border: '2px solid transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-10px)';
                e.currentTarget.style.boxShadow = `0 12px 30px ${examCategories[selectedExam].color}30`;
                e.currentTarget.style.borderColor = examCategories[selectedExam].color;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
                e.currentTarget.style.borderColor = 'transparent';
              }}>
                <div style={{ 
                  fontSize: '3.5rem',
                  color: examCategories[selectedExam].color,
                  marginBottom: '20px',
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  {tip.icon}
                </div>
                <h3 style={{ 
                  fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)',
                  fontWeight: 'bold',
                  marginBottom: '15px',
                  color: '#2c3e50'
                }}>
                  {tip.title}
                </h3>
                <p style={{ 
                  color: '#666',
                  lineHeight: '1.6',
                  margin: 0,
                  fontSize: 'clamp(0.9rem, 2vw, 1rem)'
                }}>
                  {tip.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Important Dates */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            Important Dates 2025
          </h2>

          <div style={{
            background: 'white',
            padding: 'clamp(20px, 4vw, 40px)',
            borderRadius: '20px',
            boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '20px'
            }}>
              {importantDates[selectedExam].map((item, idx) => (
                <div key={idx} style={{
                  padding: '20px',
                  background: idx % 2 === 0 ? `${examCategories[selectedExam].color}10` : '#f8f9fa',
                  borderRadius: '12px',
                  borderLeft: `5px solid ${examCategories[selectedExam].color}`,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateX(5px)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px'
                  }}>
                    <h4 style={{ 
                      fontSize: 'clamp(1rem, 2.2vw, 1.2rem)', 
                      fontWeight: 'bold',
                      color: '#2c3e50',
                      margin: 0
                    }}>
                      {item.event}
                    </h4>
                    <span style={{
                      padding: '5px 12px',
                      background: examCategories[selectedExam].color,
                      color: 'white',
                      borderRadius: '20px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      {item.status}
                    </span>
                  </div>
                  <p style={{ 
                    fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                    color: '#666',
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <Calendar size={16} style={{ color: examCategories[selectedExam].color }} />
                    {item.date}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
   
          {/* Preparation Courses */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            flexWrap: 'wrap',
            padding:'30px',
            textDecoration:'underline',
          }}>
            <Award size={32} style={{ color: examCategories[selectedExam].color }} />
            CUCET Preparation Courses
          </h2> 

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px'
          }}>
            {[
              {
                name: "Foundation Course",
                price: "7,999",
                duration: "6 Months",
                features: [
                  "Complete Syllabus Coverage",
                  "NCERT-Based Video Lectures",
                  "Study Material (PDF)",
                  "30 Mock Tests",
                  "Domain Subject Training",
                  "Doubt Clearing Sessions"
                ],
                gradient: examCategories[selectedExam].gradient
              },
              {
                name: "Premium Course",
                price: "14,999",
                duration: "9 Months",
                features: [
                  "Everything in Foundation",
                  "Live Interactive Classes",
                  "60+ Full-Length Mock Tests",
                  "Previous  Years Papers",
                  "Sectional Tests (300+)",
                  "General Test Mastery",
                  "Personal Mentorship",
                  "University Selection Guidance"
                ],
                gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                popular: true
              },
              {
                name: "Complete Package",
                price: "24,999",
                duration: "12 Months",
                features: [
                  "Everything in Premium",
                  "One-on-One Mentoring",
                  "Unlimited Mock Tests",
                  "All Domain Subjects Coverage",
                  "Language Test Training",
                  "Performance Analytics",
                  "Study Planner & Tracker",
                  "Counselling Support",
                  "24/7 Premium Support"
                ],
                gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
              }
            ].map((pkg, idx) => (
              <div key={idx} style={{
                background: 'white',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: pkg.popular ? '0 12px 40px rgba(102,126,234,0.3)' : '0 4px 15px rgba(0,0,0,0.08)',
                border: pkg.popular ? '3px solid #667eea' : '1px solid #e8e8e8',
                position: 'relative',
                transform: pkg.popular ? 'scale(1.05)' : 'scale(1)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = pkg.popular ? 'scale(1.08)' : 'scale(1.03)';
                e.currentTarget.style.boxShadow = '0 15px 45px rgba(102,126,234,0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = pkg.popular ? 'scale(1.05)' : 'scale(1)';
                e.currentTarget.style.boxShadow = pkg.popular ? '0 12px 40px rgba(102,126,234,0.3)' : '0 4px 15px rgba(0,0,0,0.08)';
              }}>
                {pkg.popular && (
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    right: '-35px',
                    background: '#FFD700',
                    color: '#333',
                    padding: '5px 40px',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    transform: 'rotate(45deg)',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                    zIndex: 1
                  }}>
                    RECOMMENDED
                  </div>
                )}
                
                <div style={{
                  background: pkg.gradient,
                  padding: '40px 30px',
                  textAlign: 'center',
                  color: 'white'
                }}>
                  <h3 style={{ 
                    fontSize: 'clamp(1.3rem, 3vw, 1.8rem)',
                    fontWeight: 'bold',
                    marginBottom: '15px'
                  }}>
                    {pkg.name}
                  </h3>
                  <div style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 'bold', marginBottom: '10px' }}>
                    ₹{pkg.price}
                  </div>
                  <div style={{ fontSize: 'clamp(1rem, 2vw, 1.1rem)', opacity: 0.9 }}>
                    {pkg.duration}
                  </div>
                </div>

                <div style={{ padding: 'clamp(20px, 4vw, 30px)' }}>
                  <ul style={{ 
                    listStyle: 'none',
                    padding: 0,
                    margin: '0 0 25px 0'
                  }}>
                    {pkg.features.map((feature, i) => (
                      <li key={i} style={{
                        padding: '12px 0',
                        borderBottom: i < pkg.features.length - 1 ? '1px solid #f0f0f0' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        color: '#555',
                        fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)'
                      }}>
                        <CheckCircle size={16} style={{ color: '#43e97b', flexShrink: 0 }} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button style={{
                    width: '100%',
                    padding: '15px',
                    borderRadius: '10px',
                    border: 'none',
                    background: pkg.gradient,
                    color: 'white',
                    fontSize: 'clamp(1rem, 2vw, 1.1rem)',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                   onClick={() => window.location.href = '/enroll'}
                  
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}>
                    Enroll Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQs */}
        <section style={{ marginBottom: '60px' }}>
          <h2 style={{ 
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', 
            fontWeight: 'bold', 
            marginBottom: '30px',
            color: '#2c3e50',
            textAlign: 'center'
          }}>
            Frequently Asked Questions
          </h2>

          <div style={{
            background: 'white',
            borderRadius: '15px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
            overflow: 'hidden'
          }}>
            {faqs.map((faq, idx) => (
              <div key={idx} style={{
                borderBottom: idx < faqs.length - 1 ? '1px solid #e8e8e8' : 'none'
              }}>
                <div
                  onClick={() => setExpandedFAQ(expandedFAQ === idx ? null : idx)}
                  style={{
                    padding: 'clamp(20px, 4vw, 30px)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    background: expandedFAQ === idx ? `${examCategories[selectedExam].color}10` : 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (expandedFAQ !== idx) {
                      e.currentTarget.style.background = '#f8f9fa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (expandedFAQ !== idx) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <h4 style={{ 
                    fontSize: 'clamp(1rem, 2.5vw, 1.3rem)',
                    fontWeight: 'bold',
                    marginBottom: '0',
                    color: examCategories[selectedExam].color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px'
                  }}>
                    <span style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <span style={{ flexShrink: 0 }}>Q{idx + 1}.</span>
                      <span>{faq.q}</span>
                    </span>
                    {expandedFAQ === idx ? (
                      <ChevronUp size={24} style={{ flexShrink: 0 }} />
                    ) : (
                      <ChevronDown size={24} style={{ flexShrink: 0 }} />
                    )}
                  </h4>
                  {expandedFAQ === idx && (
                    <p style={{ 
                      color: '#666',
                      lineHeight: '1.8',
                      margin: '15px 0 0 35px',
                      fontSize: 'clamp(0.9rem, 2vw, 1.05rem)',
                      animation: 'fadeIn 0.3s ease'
                    }}>
                      {faq.a}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section>
          <div style={{
            background: examCategories[selectedExam].gradient,
            padding: 'clamp(40px, 8vw, 60px) clamp(20px, 4vw, 40px)',
            borderRadius: '20px',
            textAlign: 'center',
            color: 'white'
          }}>
            <div style={{ fontSize: 'clamp(3rem, 8vw, 4rem)', marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
              <GraduationCap size={64} />
            </div>
            <h2 style={{ 
              fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
              fontWeight: 'bold',
              marginBottom: '20px'
            }}>
              Begin Your Journey to Top Universities!
            </h2>
            <p style={{ 
              fontSize: 'clamp(1rem, 2.5vw, 1.3rem)',
              marginBottom: '35px',
              opacity: 0.95,
              maxWidth: '700px',
              margin: '0 auto 35px'
            }}>
              Join thousands of successful candidates and secure admission to your dream university
            </p>
            <div style={{ 
              display: 'flex',
              gap: '20px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button 
                onClick={() => alert('Enrollment functionality')}
                style={{
                  background: 'white',
                  color: examCategories[selectedExam].color,
                  padding: 'clamp(12px, 3vw, 18px) clamp(30px, 5vw, 45px)',
                  borderRadius: '30px',
                  border: 'none',
                  fontSize: 'clamp(1rem, 2vw, 1.2rem)',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 12px 30px rgba(0,0,0,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.2)';
                }}>
                Start CUET Preparation Now
              </button>
              <button style={{
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                padding: 'clamp(12px, 3vw, 18px) clamp(30px, 5vw, 45px)',
                borderRadius: '30px',
                border: '2px solid white',
                fontSize: 'clamp(1rem, 2vw, 1.2rem)',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onClick={() => alert('Download functionality')}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = examCategories[selectedExam].color;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                e.currentTarget.style.color = 'white';
              }}>
                Download Free Mock Tests
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* Footer Note */}
      <div style={{
        background: '#2c3e50',
        color: 'white',
        padding: '30px 20px',
        textAlign: 'center',
        marginTop: '60px'
      }}>
        <p style={{ margin: '0 0 10px', fontSize: 'clamp(1rem, 2vw, 1.1rem)' }}>
          © 2025 Olewin Academy - All Rights Reserved
        </p>
        <p style={{ margin: 0, opacity: 0.8, fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)' }}>
          For official information, visit: <a href="https://cuet.nta.nic.in" target="_blank" rel="noopener noreferrer" style={{ color: '#43e97b', textDecoration: 'none' }}>CUET Official Website</a>
        </p>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @media (max-width: 768px) {
          .table-container {
            overflow-x: auto;
          }
        }
      `}</style>
    </div>
  );
};

export default CUETExamPage;